import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'

import { getPlaceFolder } from '../../../api'
import usePermissions from '../../Core/usePermissions'

import styles from './styles.module.scss'

import AppNavbar from '../../Core/AppNavbar'
import UploadDocuments from '../UploadDocuments'
import FolderReviewers from '../FolderReviewers'
import FolderAccess from '../FolderAccess'

const emptyFolder = {
  documents: [],
  assign_employees: [],
}

const FolderContent = forwardRef(({companyId, placeId, folderId, canManage, employees, onUpdate}, ref) => {
  const { t } = useTranslation()
  const permissions = usePermissions([ {
    name: 'can_manage_documents',
    permission: 'places_can_manage_documents',
  }])

  const [activeTab, setActiveTab] = useState('documents')
  const [{data: folder}, setFolder] = useState({loading: false, data: emptyFolder})

  const options = [{
      title: t('Documents'),
      key: 'documents',
      badge: 0,
    },
    ...(permissions.can_manage_documents ? [{
      title: t('Access control'),
      key: 'access',
      badge: 0,
    }] : [])
  ]

  useImperativeHandle(ref, () => ({
    update: (upadates) => {
      setFolder(prev => ({...prev, data: {...prev.data, ...upadates}}))
    },
  }))

  useEffect(() => {
    if (companyId && placeId && folderId) {
      getFolder()
    }
  }, [companyId, placeId, folderId])

  const getFolder = async () => {
    setFolder(prev => ({...prev, loading: true}))
    const res = await getPlaceFolder(companyId, placeId, folderId)
    if (res) {
      setFolder({loading: false, data: res})
      return res
    }
    setFolder({loading: false, data: emptyFolder})
  }

  const handleSelectTab = (key) => {
    setActiveTab(key)
  }

  const handleUpdateFolder = async () => {
    const updatedFolder = await getFolder()
    onUpdate({
      id: folderId,
      assign_employees_count: updatedFolder.assign_employees.length,
      documents_count: updatedFolder.documents.length,
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{folder.name}</div>
        <div className={styles.description}>{folder.description}</div>
      </div>
      <AppNavbar
        options={options}
        selected={activeTab}
        className={styles.navbar}
        highlighted={false}
        onSelect={handleSelectTab}
      />
      {
        ((tab) => {
          switch (tab) {
            case 'documents':
              return (
                <div className={styles.documentsContainer}>
                  <UploadDocuments
                    companyId={companyId}
                    placeId={placeId}
                    folderId={folderId}
                    onUploaded={handleUpdateFolder}
                    onRemove={handleUpdateFolder}
                    documents={folder.documents}
                    canManage={canManage} />
                  <FolderReviewers
                    reviewers={folder.assign_employees} />
                </div>
              )
            case 'access':
              return (
                <div style={{paddingTop: 14}}>
                  <FolderAccess
                    companyId={companyId}
                    placeId={placeId}
                    folderId={folderId}
                    employees={employees}
                    assignedEmployees={folder.assign_employees}
                    onChangeAccess={handleUpdateFolder} />
                </div>
              )
            default:
              return null
          }
        })(activeTab)
      }
    </div>
  )
})

export default FolderContent
